import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Stack, Typography } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";

import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import AppButton from "../components/buttons/AppButton";
import StandardTable, { TableColumn } from "../components/common/StandardTable";
import ConfirmDialog from "../components/common/ConfirmDialog";
import StatusChip from "../components/common/StatusChip";
import RowActionMenu, {
  RowActionDef,
} from "../components/common/RowActionMenu";
import UploadBankStatementModal from "../components/bankTransactionsStaging/UploadBankStatementModal";
import PostStagedTransactionModal from "../components/bankTransactionsStaging/PostStagedTransactionModal";

import { useAppDispatch, useAppSelector } from "../redux/store";
import { usePermissions } from "../hooks/usePermissions";
import {
  getStagedTransactions,
  deleteStagedTransaction,
  resetStagingStatusAction,
} from "../redux/actions/bankTransactionStagingActions";
import { BankTransactionStagingResponseDto } from "../shared/dtos/bankTransaction/BankTransactionStagingResponseDto";

const BankTransactionsStagingPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  const {
    stagedTransactions,
    pagination,
    loadingStagedTransactions,
    errorLoadingStagedTransactions,
    serverStatus,
    loadingStagedTransactionDelete,
    loadingStagedTransactionPost,
    errorPostingStagedTransaction,
  } = useAppSelector((state) => state.bankTransactionStaging);

  const [pageConfig, setPageConfig] = useState({ page: 1, limit: 10 });
  const [selectedRecord, setSelectedRecord] =
    useState<BankTransactionStagingResponseDto | null>(null);

  // Modals
  const [isUploadOpen, setIsUploadOpen] = useState<boolean>(false);
  const [isPostOpen, setIsPostOpen] = useState<boolean>(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState<boolean>(false);

  // Permissions
  const canCreate = hasPermission("bank_staging:create");
  const canDelete = hasPermission("bank_staging:update");

  const fetchPendingList = useCallback((): void => {
    dispatch(getStagedTransactions(pageConfig.page, pageConfig.limit));
  }, [dispatch, pageConfig.page, pageConfig.limit]);

  useEffect(() => {
    fetchPendingList();
  }, [fetchPendingList]);

  const handleOpenPost = (
    selectedRecord: BankTransactionStagingResponseDto,
  ): void => {
    setSelectedRecord(selectedRecord);
    setIsPostOpen(true);
  };

  const handleOpenDelete = (
    selectedRecord: BankTransactionStagingResponseDto,
  ): void => {
    setSelectedRecord(selectedRecord);
    setIsDeleteOpen(true);
  };

  // Handle auto-close on success
  useEffect(() => {
    if (!serverStatus) return;
    const { operation, status } = serverStatus;

    if (operation === "upload" && status === 201) setIsUploadOpen(false);
    if (operation === "post" && status === 200) setIsPostOpen(false);
    if (operation === "delete" && status === 200) setIsDeleteOpen(false);

    dispatch(resetStagingStatusAction());
  }, [serverStatus, dispatch]);

  const columns = useMemo<TableColumn<BankTransactionStagingResponseDto>[]>(
    () => [
      { id: "bankDate", label: "Date", minWidth: 100 },
      {
        id: "rawReference",
        label: "Bank Ref",
        minWidth: 130,
        format: (v) => (
          <Typography sx={{ fontWeight: "bold", fontSize: "0.85rem" }}>
            {String(v)}
          </Typography>
        ),
      },
      { id: "rawDescription", label: "Narrative", minWidth: 200 },
      {
        id: "amount",
        label: "Amount (KES)",
        minWidth: 100,
        format: (v) => Number(v).toLocaleString(),
      },
      {
        id: "matchConfidence",
        label: "Match Status",
        minWidth: 150,
        format: (val, row) => (
          <Stack direction="column">
            <StatusChip
              status={
                val === "EXACT" ? ("MATCHED" as any) : ("UNMATCHED" as any)
              }
            />
            {val === "EXACT" && (
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", mt: 0.5 }}
              >
                {row.matchedStudentName} ({row.matchedAdmissionNumber})
              </Typography>
            )}
          </Stack>
        ),
      },
    ],
    [],
  );

  const renderRowActions = (
    row: BankTransactionStagingResponseDto,
  ): React.ReactNode => {
    const menuActions: RowActionDef[] = [
      {
        label: "Post to Ledger",
        icon: <CheckCircleOutlineIcon fontSize="small" />,
        onClick: () => handleOpenPost(row),
        show: canCreate,
      },
      {
        label: "Discard (e.g. Bank Charges)",
        icon: <DeleteOutlineIcon fontSize="small" />,
        color: "error",
        onClick: () => handleOpenDelete(row),
        show: canDelete,
      },
    ];

    return <RowActionMenu menuActions={menuActions} />;
  };

  return (
    <PageContainer
      title="Bank Reconciliation"
      subtitle="Upload bank statements, verify smart-matches, and post payments to student ledgers."
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            startIcon={<CloudUploadIcon />}
            onClick={() => setIsUploadOpen(true)}
          >
            Upload Statement
          </AppButton>
        )
      }
    >
      <PageToolbar searchPlaceholder="Filter is disabled for staging area." />

      <StandardTable<BankTransactionStagingResponseDto>
        columns={columns}
        data={stagedTransactions}
        loading={loadingStagedTransactions}
        error={errorLoadingStagedTransactions}
        onRetry={fetchPendingList}
        page={pageConfig.page}
        count={pagination?.totalElements || 0}
        rowsPerPage={pageConfig.limit}
        onPageChange={(newPage) =>
          setPageConfig((prev) => ({ ...prev, page: newPage }))
        }
        onRowsPerPageChange={(newLimit) =>
          setPageConfig({ ...pageConfig, limit: newLimit, page: 1 })
        }
        rowAction={renderRowActions}
      />

      {/* MODALS */}
      {isUploadOpen && (
        <UploadBankStatementModal
          open={isUploadOpen}
          onClose={() => setIsUploadOpen(false)}
        />
      )}

      {isPostOpen && selectedRecord && (
        <PostStagedTransactionModal
          open={isPostOpen}
          onClose={() => setIsPostOpen(false)}
          stagingRecord={selectedRecord}
          loading={loadingStagedTransactionPost}
          error={errorPostingStagedTransaction}
        />
      )}

      {isDeleteOpen && selectedRecord && (
        <ConfirmDialog
          open={isDeleteOpen}
          onClose={() => setIsDeleteOpen(false)}
          onConfirm={() => dispatch(deleteStagedTransaction(selectedRecord.id))}
          title="Discard Transaction"
          description={`Are you sure you want to discard Bank Ref ${selectedRecord.rawReference}? This is usually done for non-student deposits or bank charges.`}
          loading={loadingStagedTransactionDelete}
        />
      )}
    </PageContainer>
  );
};

export default BankTransactionsStagingPage;
